import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import type { Action, Resource } from '@/context/permissions';

interface PermissionRouteProps {
    /** The component to render when the permission check passes */
    element: React.ReactNode;
    /** Resource to check, e.g. 'product', 'order' */
    resource: Resource;
    /** Action to check, e.g. 'view', 'create' */
    action?: Action;
    /** Where to redirect on failure — defaults to /dashboard */
    fallbackPath?: string;
}

/**
 * PermissionRoute
 * ---------------
 * Drop-in replacement for the existing PrivateRoute when you also want
 * a fine-grained permission check (not just authentication).
 *
 * Admin role bypasses all permission checks and always gets access —
 * mirrors the nassfoods_admin ProtectedRoute pattern:
 *   if (userDetail?.role?.name?.toLowerCase() === 'admin') return <Outlet />;
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({
    element,
    resource,
    action = 'view',
    fallbackPath,
}) => {
    const location = useLocation();
    const { isAdmin, hasPermission, isLoading } = usePermissions();

    // console.log(isAdmin, 'isAdmin');
    // console.log(hasPermission(resource, action), 'hasPermission');
    // console.log(action, 'action');
    // console.log(resource, 'resource');
    // console.log(isLoading, 'isLoading');



    // 2. Admin always has full access — bypass all checks
    if (isAdmin) {
        return <>{element}</>;
    }

    // 3. Check specific permission
    if (!hasPermission(resource, action)) {
        showToastNotification('danger', 'You do not have permission to access this page.');

        // If we are already on a private route and fail, go to 403 instead of dashboard
        // to avoid potential redirect loops.
        const targetFallback = fallbackPath || RoutesLink.forbidden_403;
        return <Navigate to={targetFallback} state={{ from: location }} replace />;
    }

    return <>{element}</>;
};

export default PermissionRoute;
